import {
  AaasSubscriptionType,
  CustomerInfo,
  ExternalPricingPackage,
  ExternalSubscription,
  Invoice,
  Package,
  PaymentMethod,
  PromotionCode,
  SubscriptionTypeEnum,
} from '@va/aaas/shared/types';
import { apiGateway } from '@va/api-client';
import { PaginatedResponse, PayloadResponse } from '@va/types/requests';

export type PricingPlansResponse = PaginatedResponse<PricingPlansPayload>;
export type PricingPlansResponseV2 = PaginatedResponse<PricingPlan[]>;

export type PricingPlanPrice = {
  stripePriceId: string;
  aaasPackageId: string;
  billingInterval: SubscriptionTypeEnum;
  recommended: boolean;
  default: boolean;
  currency: string;
  unitAmount: number;
  taxBehaviour: string;
};

export type PricingPlan = {
  stripeProductId: string;
  description: string;
  name: string;
  recommended: boolean;
  touchpoints: number;
  prices: PricingPlanPrice[];
};

export type PricingPlansPayload = {
  monthly: Package[];
  yearly: Package[];
};

type CreateCheckoutSessionPayload = {
  stripePriceId: string;
  promotionCodeId?: string;
};

export const getCustomer = async (subscriptionType: AaasSubscriptionType, websiteId: string) => {
  let url = '/3as-payments/v1/customer';

  if (subscriptionType === AaasSubscriptionType.Website) {
    url += `?websiteId=${websiteId}`;
  }

  const { payload } = await apiGateway.get<PaginatedResponse<CustomerInfo | null>>(url);

  return payload;
};

const createWebsiteCustomer = async (
  websiteId: string,
  aaasSubscriptionId: string,
  payload: Omit<CustomerInfo, 'id'>,
) => {
  const { payload: createdCustomer } = await apiGateway.post<PaginatedResponse<Omit<CustomerInfo, 'id'>>>(
    `/3as-payments/v1/websites/${websiteId}/customers`,
    { ...payload, aaasSubscriptionId: aaasSubscriptionId },
  );

  return createdCustomer;
};

const createINTPCCustomer = async (aaasSubscriptionId: string, payload: Omit<CustomerInfo, 'id'>) => {
  const { payload: createdCustomer } = await apiGateway.post<PaginatedResponse<Omit<CustomerInfo, 'id'>>>(
    `/3as-payments/v1/customers`,
    { ...payload, aaasSubscriptionId: aaasSubscriptionId },
  );

  return createdCustomer;
};

export const updateCustomer = async (customerId: string, payload: Omit<CustomerInfo, 'id'>) => {
  const { payload: updatedCustomer } = await apiGateway.patch<PaginatedResponse<Omit<CustomerInfo, 'id'>>>(
    `/3as-payments/v1/customers/${customerId}`,
    payload,
  );

  return updatedCustomer;
};

export const getStripeClientSecret = async (customerId: string, payload: CreateCheckoutSessionPayload) => {
  const {
    payload: { clientSecret },
  } = await apiGateway.post<PaginatedResponse<{ clientSecret: string }>>(
    `/3as-payments/v1/customers/${customerId}/subscriptions/checkout`,
    payload,
  );

  return clientSecret;
};

export const getExternalSubscription = async (customerId: string) => {
  const { payload } = await apiGateway.get<PaginatedResponse<ExternalSubscription>>(
    `/3as-payments/v1/customers/${customerId}/subscriptions/active`,
  );

  return payload;
};

export type GetPricingPlansApiResponse = PayloadResponse<ExternalPricingPackage[], { defaultCurrency: string }>;

export const getPricingPlans = async (aaasSubscriptionId: string): Promise<GetPricingPlansApiResponse> => {
  return await apiGateway.get<GetPricingPlansApiResponse>(
    `/3as-payments/v1/packages?subscriptionId=${aaasSubscriptionId}`,
  );
};

export const previewSubscriptionPackageChange = async ({
  stripePriceId,
  customerId,
  subscriptionId,
  promotionCodeId,
}: {
  customerId: string;
  subscriptionId: string;
  stripePriceId: string;
  promotionCodeId?: string;
}) => {
  const { payload } = await apiGateway.post<PaginatedResponse<Invoice>>(
    `/3as-payments/v1/customers/${customerId}/subscriptions/${subscriptionId}/change-package-invoice-preview`,
    { stripePriceId, promotionCodeId: promotionCodeId },
  );

  return payload;
};

export const changeSubscriptionPackage = async ({
  customerId,
  stripePriceId,
  subscriptionId,
  promotionCodeId,
}: {
  customerId: string;
  subscriptionId: string;
  stripePriceId: string;
  promotionCodeId?: string;
}) => {
  const { payload } = await apiGateway.post<PaginatedResponse<ExternalSubscription>>(
    `/3as-payments/v1/customers/${customerId}/subscriptions/${subscriptionId}/change-package`,
    {
      stripePriceId: stripePriceId,
      promotionCodeId: promotionCodeId,
    },
  );

  return payload;
};

export const getInvoices = async (customerId: string) => {
  const { payload } = await apiGateway.get<PaginatedResponse<Invoice[]>>(
    `/3as-payments/v1/customers/${customerId}/invoices`,
  );

  return payload;
};

export const getPromotionCode = async (code: string) => {
  const { payload } = await apiGateway.post<PaginatedResponse<PromotionCode>>(
    `/3as-payments/v1/promotion-codes/retrieve`,
    {
      code: code,
    },
  );

  return payload;
};

export const cancelSubscriptionAtBillingCycleEnd = async (customerId: string, subscriptionId: string) => {
  return apiGateway.post(`/3as-payments/v1/customers/${customerId}/subscriptions/${subscriptionId}/cancel`, {});
};

export const resumeSubscription = async (customerId: string, subscriptionId: string) => {
  return await apiGateway.post(`/3as-payments/v1/customers/${customerId}/subscriptions/${subscriptionId}/resume`, {});
};

export const getPaymentMethods = async (customerId: string) => {
  const { payload } = await apiGateway.get<PaginatedResponse<PaymentMethod[]>>(
    `/3as-payments/v1/customers/${customerId}/payment-methods`,
  );

  return payload;
};

export const createBillingPortalSession = async (customerId: string) => {
  const { payload } = await apiGateway.get<PaginatedResponse<{ url: string } | null>>(
    `/3as-payments/v1/customers/${customerId}/billing-portal-session`,
  );

  return payload?.url ?? '';
};

export const getUpcomingInvoice = async (customerId: string) => {
  const { payload } = await apiGateway.get<PaginatedResponse<Invoice | null>>(
    `/3as-payments/v1/customers/${customerId}/invoices/upcoming`,
  );
  return payload;
};

export const fetchConnectAccountId = async () => {
  const { payload } = await apiGateway.get<
    PaginatedResponse<{ accountId: string | null; stripePublishableApiKey: string }>
  >('/3as-payments/v1/connect/account');
  return payload;
};

export const AaasPaymentsApi = {
  fetchConnectAccountId,
  getExternalSubscription,
  getCustomer,
  createWebsiteCustomer,
  createINTPCCustomer,
  updateCustomer,
  getStripeClientSecret,
  previewSubscriptionPackageChange,
  changeSubscriptionPackage,
  getPaymentMethods,
  getPromotionCode,
  getPricingPlans,
};
